'use client';

import * as Sentry from '@sentry/nextjs';

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  // Capture exception to Sentry
  if (error) {
    Sentry.captureException(error);
  }

  return (
    <html>
      <head>
        <title>Application Error</title>
        <style>{`
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
            display: flex;
            align-items: center;
            justify-content: center;
            min-height: 100vh;
            margin: 0;
            background: linear-gradient(135deg, #e1f5ee 0%, #ffffff 50%, #f3f6f5 100%);
            color: #1a1a1a;
          }
          .container {
            text-align: center;
            max-width: 600px;
            padding: 40px;
          }
          h1 {
            font-size: 32px;
            margin-bottom: 20px;
            color: #0f6e56;
          }
          p {
            font-size: 16px;
            color: #666;
            margin-bottom: 30px;
            line-height: 1.6;
          }
          .button-group {
            display: flex;
            gap: 16px;
            justify-content: center;
            flex-wrap: wrap;
          }
          button, a {
            padding: 12px 32px;
            font-size: 16px;
            font-weight: 600;
            border: none;
            border-radius: 8px;
            cursor: pointer;
            transition: all 0.3s ease;
            text-decoration: none;
            display: inline-block;
          }
          .btn-primary {
            background-color: #1d9e75;
            color: white;
          }
          .btn-primary:hover {
            background-color: #0f6e56;
          }
          .btn-secondary {
            border: 2px solid rgba(29, 158, 117, 0.3);
            color: #0f6e56;
            background-color: transparent;
          }
          .btn-secondary:hover {
            background-color: #e1f5ee;
          }
          .error-details {
            margin-top: 40px;
            padding: 16px;
            background-color: #fee;
            border: 1px solid #fcc;
            border-radius: 8px;
            text-align: left;
            font-family: monospace;
            font-size: 12px;
            color: #c33;
            white-space: pre-wrap;
            word-break: break-word;
            max-height: 200px;
            overflow-y: auto;
          }
        `}</style>
      </head>
      <body>
        <div className="container">
          <h1>Application Error</h1>
          <p>
            We encountered an unexpected error. Our team has been notified and we&apos;re working on a fix.
          </p>

          <div className="button-group">
            <button onClick={reset} className="btn-primary">
              Try Again
            </button>
            <a href="/" className="btn-secondary">
              Go Home
            </a>
          </div>

          {process.env.NODE_ENV === 'development' && error?.message && (
            <div className="error-details">
              {error.message}
              {error.digest && `\n\nDigest: ${error.digest}`}
            </div>
          )}
        </div>
      </body>
    </html>
  );
}
